import * as React from "react";
import {
  flexRender,
  useTable,
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type RowData,
  type SortingState
} from "@tanstack/react-table";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Search
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { PHONE_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { features, type AppTableFeatures, type MobileSlot } from "./features";

interface DataTableProps<TData extends RowData> {
  columns: ColumnDef<AppTableFeatures, TData>[];
  data: TData[];
  /** Column id the search box filters on. Omit to hide the search box. */
  filterColumn?: string;
  filterPlaceholder?: string;
  /** Extra classes merged onto the search box's wrapper - e.g. a narrower
   *  max-width on a page whose toolbar also has several dropdown filters
   *  competing for space (see UserManagement.tsx). Defaults to this
   *  component's own sm:max-w-xs when omitted. */
  filterClassName?: string;
  /** Rendered on the left of the toolbar - e.g. segmented status tabs. */
  toolbarStart?: React.ReactNode;
  /** Rendered directly after the search box - e.g. extra filter dropdowns
   *  that narrow the same rows the search does. */
  toolbarAfterSearch?: React.ReactNode;
  /** Rendered on the right of the toolbar - e.g. a primary action button. */
  toolbarEnd?: React.ReactNode;
  emptyMessage?: string;
  pageSize?: number;
  isLoading?: boolean;
  /** When set, this table's page/sort/search state is remembered in
   *  sessionStorage under this key and restored on mount - so navigating
   *  away (e.g. opening a row's detail view) and back doesn't reset the
   *  employee to page 1 with sorting/search cleared. Give each DataTable on
   *  a page its own distinct key (e.g. "my-requests:travel", "my-requests:
   *  advances") - omit for a table where this doesn't matter (nothing to
   *  navigate away to, or it's always freshly filtered anyway). Cleared
   *  automatically if the shape of what's stored ever fails to parse. */
  persistKey?: string;
  /** When given, a row this returns true for gets a persistent highlight
   *  tint (desktop row and phone card both) - e.g. the one request/advance
   *  a notification's "View Request" link deep-linked straight to (see
   *  RequestDetailDialog/AdvanceDetailDialog's own `highlight` prop and
   *  MyApprovals.tsx/MyRequests.tsx/ApproverQueue.tsx), kept highlighted
   *  in the table after that detail sheet closes so the employee can
   *  still find which row it was - clears whenever the caller's own state
   *  driving this predicate clears (not something this component tracks
   *  on its own). Omit for a table with nothing to highlight. */
  highlightRow?: (row: TData) => boolean;
}

interface PersistedTableState {
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  pagination: PaginationState;
}

function loadPersistedState(key: string): PersistedTableState | null {
  try {
    const raw = sessionStorage.getItem(`asmara.table.${key}`);
    return raw ? (JSON.parse(raw) as PersistedTableState) : null;
  } catch {
    return null;
  }
}

function savePersistedState(key: string, state: PersistedTableState): void {
  try {
    sessionStorage.setItem(`asmara.table.${key}`, JSON.stringify(state));
  } catch {
    // Best-effort - a full/blocked sessionStorage just means this table
    // won't remember its state, never a reason to break the page.
  }
}

export function DataTable<TData extends RowData>({
  columns,
  data,
  filterColumn,
  filterPlaceholder = "Search…",
  filterClassName,
  toolbarStart,
  toolbarAfterSearch,
  toolbarEnd,
  emptyMessage = "No results.",
  pageSize = 10,
  isLoading = false,
  persistKey,
  highlightRow
}: DataTableProps<TData>) {
  // Restored once, on first mount, when persistKey is given - lazy
  // initializers so this read happens exactly once, not on every render.
  const restored = React.useMemo(
    () => (persistKey ? loadPersistedState(persistKey) : null),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const [sorting, setSorting] = React.useState<SortingState>(
    restored?.sorting ?? []
  );
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    restored?.columnFilters ?? []
  );
  const [rowSelection, setRowSelection] = React.useState({});
  const [pagination, setPagination] = React.useState<PaginationState>(
    restored?.pagination ?? { pageIndex: 0, pageSize }
  );

  React.useEffect(() => {
    if (!persistKey) return;
    savePersistedState(persistKey, { sorting, columnFilters, pagination });
  }, [persistKey, sorting, columnFilters, pagination]);

  const table = useTable({
    features,
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    state: { sorting, columnFilters, rowSelection, pagination }
  });

  // Clamps a restored (or otherwise stale) pageIndex once the real page
  // count is known - e.g. a persisted "page 5" from a previous visit, but
  // this data set (or the current filter) now only has 2 pages. v9's core
  // doesn't auto-correct this on its own (that only happens inside the
  // worker helper, not the plain client-side path this table uses), so an
  // out-of-range page would otherwise render as a silently empty table.
  const pageCount = table.getPageCount();
  React.useEffect(() => {
    if (pageCount > 0 && pagination.pageIndex > pageCount - 1) {
      setPagination((p) => ({ ...p, pageIndex: pageCount - 1 }));
    }
  }, [pageCount, pagination.pageIndex]);

  const rows = table.getRowModel().rows;
  const isPhone = useMediaQuery(PHONE_QUERY);

  // Each column's slot in the phone card layout (see MobileSlot in
  // features.ts for the defaults), resolved once per column set.
  const slots = React.useMemo(() => {
    const map: Record<string, MobileSlot> = {};
    let titleTaken = false;
    for (const col of table.getAllLeafColumns()) {
      const meta = col.columnDef.meta;
      let slot: MobileSlot | undefined = meta?.mobile;
      if (!slot) {
        if (col.id === "status") slot = "badge";
        else if (!meta?.label) slot = "actions";
        else if (!titleTaken) slot = "title";
        else slot = "field";
      }
      if (slot === "title") titleTaken = true;
      map[col.id] = slot;
    }
    return map;
    // `table` is stable across renders; its columns change only with `columns`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  // Cards have no clickable column headers, so on phones the sortable
  // columns are offered from a toolbar menu instead.
  const sortableColumns = isPhone
    ? table
        .getAllLeafColumns()
        .filter((col) => col.getCanSort() && col.columnDef.meta?.label)
    : [];

  const toolbar = (toolbarStart ||
    filterColumn ||
    toolbarAfterSearch ||
    toolbarEnd ||
    sortableColumns.length > 0) && (
    <div className="flex flex-wrap items-center gap-2">
      {toolbarStart}
      {filterColumn && (
        // Full-width row of its own on phones (order-last), inline on
        // wider screens.
        <div
          className={cn(
            "relative order-last w-full sm:order-none sm:max-w-xs",
            filterClassName
          )}
        >
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={
              (table.getColumn(filterColumn)?.getFilterValue() as string) ?? ""
            }
            onChange={(e) =>
              table.getColumn(filterColumn)?.setFilterValue(e.target.value)
            }
            placeholder={filterPlaceholder}
            className="h-9 pl-9"
          />
        </div>
      )}
      {toolbarAfterSearch}
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {sortableColumns.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
              >
                <ArrowUpDown className="h-4 w-4" />
                <span className="sr-only">Sort</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                Sort by
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {sortableColumns.map((col) => {
                const sorted = col.getIsSorted();
                return (
                  <DropdownMenuItem
                    key={col.id}
                    className="justify-between gap-6"
                    onSelect={(e) => {
                      // Keep the menu open so tapping again flips direction.
                      e.preventDefault();
                      col.toggleSorting(sorted === "asc");
                    }}
                  >
                    {col.columnDef.meta?.label}
                    {sorted === "asc" ? (
                      <ArrowUp className="h-3.5 w-3.5" />
                    ) : sorted === "desc" ? (
                      <ArrowDown className="h-3.5 w-3.5" />
                    ) : null}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {toolbarEnd}
      </div>
    </div>
  );

  const paginationBar = (
    <div className="flex items-center justify-between gap-2">
      <p className="text-xs text-muted-foreground">
        {table.getFilteredRowModel().rows.length} row
        {table.getFilteredRowModel().rows.length === 1 ? "" : "s"}
      </p>
      <div className="flex items-center gap-2">
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {/* v9 exposes selected state as `table.state`, not `getState()`. */}
          Page {(table.state.pagination?.pageIndex ?? 0) + 1} of{" "}
          {Math.max(1, table.getPageCount())}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Previous</span>
          <span className="sr-only sm:hidden">Previous page</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          <span className="hidden sm:inline">Next</span>
          <span className="sr-only sm:hidden">Next page</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Phone only: pinned to the top of the page's scroll area (sticky -
         the ancestor flex-1 overflow-y-auto div in App.tsx's AppShell is
         already the one scroll container every page uses) so the toolbar
         and pagination stay in view while only the card list beneath
         scrolls. A solid bg is required since sticky content still sits in
         normal flow - without it, cards scrolling underneath would show
         through - and a bottom border marks where the pinned block ends,
         since it would otherwise look like it's cutting cards off mid-
         scroll with no visible edge. */}
      <div
        className={cn(
          isPhone &&
            toolbar &&
            "sticky top-0 z-10 isolate space-y-3 border-b bg-background pb-3 [transform:translateZ(0)]"
        )}
      >
        {toolbar}
        {isPhone && paginationBar}
      </div>

      {isPhone ? (
        // Phone: one card per row - title + status badge on top, the other
        // labelled columns as a two-column label/value grid, action cells in
        // a footer. Same cell renderers as the table, so each page's column
        // defs drive both layouts with no per-page card markup.
        <div className="space-y-3">
          {isLoading ? (
            <p className="rounded-lg border py-10 text-center text-sm text-muted-foreground">
              Loading…
            </p>
          ) : rows.length ? (
            rows.map((row) => {
              const cells = row.getVisibleCells();
              const inSlot = (slot: MobileSlot) =>
                cells.filter((c) => slots[c.column.id] === slot);
              const title = inSlot("title");
              const badges = inSlot("badge");
              const fields = inSlot("field");
              const actions = inSlot("actions");
              return (
                <div
                  key={row.id}
                  className={cn(
                    "space-y-3 rounded-lg border bg-card p-4 text-sm shadow-sm",
                    highlightRow?.(row.original) &&
                      "border-primary bg-primary/10 ring-2 ring-primary"
                  )}
                >
                  {(title.length > 0 || badges.length > 0) && (
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1 text-[0.9375rem]">
                        {title.map((cell) => (
                          <React.Fragment key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                      {badges.map((cell) => (
                        <div key={cell.id} className="shrink-0">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {fields.length > 0 && (
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                      {fields.map((cell) => (
                        <div
                          key={cell.id}
                          className={cn(
                            "min-w-0",
                            cell.column.columnDef.meta?.fullWidth &&
                              "col-span-2"
                          )}
                        >
                          <dt className="text-xs text-muted-foreground">
                            {cell.column.columnDef.meta?.label}
                          </dt>
                          {/* Table cells often use whitespace-nowrap (dates,
                             ranges) - allowed to wrap in a half-width card
                             column instead of overflowing it. */}
                          <dd className="mt-0.5 break-words [&_*]:whitespace-normal">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {actions.length > 0 && (
                    <div className="-mx-4 flex flex-wrap items-center justify-end gap-2 border-t px-4 pt-3">
                      {actions.map((cell) => (
                        <React.Fragment key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <p className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </p>
          )}
        </div>
      ) : (
        // Tablet/desktop: a real table. If a wide table still doesn't fit
        // (e.g. a tablet with the sidebar expanded), it scrolls sideways
        // inside this bordered card rather than squeezing columns - via the
        // shared ScrollArea so the horizontal scrollbar matches the rest of
        // the app instead of the browser's default overflow bar.
        <ScrollArea className="rounded-md border">
          <Table className="min-w-[40rem]">
            <TableHeader className="bg-muted/50">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="h-10 text-xs font-medium uppercase tracking-wide"
                    >
                      {/* flexRender()'s return type includes `undefined`, which
                         TS won't accept as a standalone JSX element (<X/>) but
                         is fine as ordinary {expression} children - used that
                         way here rather than the <table.FlexRender/> component. */}
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-sm text-muted-foreground"
                  >
                    Loading…
                  </TableCell>
                </TableRow>
              ) : rows.length ? (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={cn(
                      highlightRow?.(row.original) &&
                        "bg-primary/15 hover:bg-primary/20"
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-3">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center text-sm text-muted-foreground"
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      )}

      {!isPhone && paginationBar}
    </div>
  );
}
